/**
 * Normalizes presentation data so both old-format (items = array of slide objects
 * with layout JSON strings in inches) and wizard-format (items = object with
 * description/report_date_context/slides) produce a consistent structure.
 *
 * Normalized shape:
 *   {
 *     ...presentation,
 *     slides_count: number,
 *     normalized_items: {
 *       description: string,
 *       report_date_context: string,
 *       slides: [{ elements: [{ type, x, y, width, height, text, ... }] }]
 *     }
 *   }
 */

// PowerPoint uses 96 DPI; layout inches * 96 = pixels
const INCHES_TO_PX = 96;

/**
 * Convert old-format row-based data into column-based format used by wizard elements.
 *
 * Old format data (rows):
 *   [{ col1: val1, col2: val2, ... }, ...]
 *
 * Wizard column format:
 *   [{ header: "col1", variable: "No", values: [{ value: "val1", variable: "No" }, ...] }, ...]
 */
function convertRowDataToColumns(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  const headers = Object.keys(data[0]);
  return headers.map((header) => ({
    header,
    variable: 'No',
    values: data.map((row) => ({
      value: row[header] != null ? String(row[header]) : '',
      variable: 'No',
    })),
  }));
}

/**
 * Build chart series and categories from old-format row data.
 * Assumes first column is categories/labels, remaining columns are series.
 *
 * Returns { categories: string[], series: [{ name, values }] }
 */
function buildChartDataFromRows(data) {
  if (!Array.isArray(data) || data.length === 0) return { categories: [], series: [] };
  const headers = Object.keys(data[0]);
  if (headers.length < 2) return { categories: [], series: [] };
  const labelKey = headers[0];
  const categories = data.map((row) => String(row[labelKey] ?? ''));
  const series = headers.slice(1).map((key) => ({
    name: key,
    values: data.map((row) => Number(row[key]) || 0),
  }));
  return { categories, series };
}

/**
 * Convert an old-format slide object into the wizard slide format.
 *
 * Old format slide:
 *   { id, title, source, prompt, query, chart, data, layout (JSON string in inches),
 *     comment, has_comment, visualization }
 *
 * Wizard slide format:
 *   { elements: [{ type, x, y, width, height, text, data_source, query, visualization, ... }] }
 */
function convertOldSlide(slide) {
  const elements = [];

  // Parse layout JSON string → position info in inches
  let layout = {};
  if (typeof slide.layout === 'string') {
    try {
      layout = JSON.parse(slide.layout);
    } catch {
      layout = {};
    }
  } else if (slide.layout && typeof slide.layout === 'object') {
    layout = slide.layout;
  }

  // Helper: convert inch-based rect to pixel-based props
  const toPixels = (rect) => {
    if (!rect) return { x: 0, y: 0, width: 0, height: 0 };
    return {
      x: Math.round((Number(rect.x) || 0) * INCHES_TO_PX),
      y: Math.round((Number(rect.y) || 0) * INCHES_TO_PX),
      width: Math.round((Number(rect.width) || 0) * INCHES_TO_PX),
      height: Math.round((Number(rect.height) || 0) * INCHES_TO_PX),
    };
  };

  // Title element
  if (layout.title || slide.title) {
    const pos = toPixels(layout.title);
    elements.push({
      type: 'title',
      ...pos,
      text: slide.title || '',
      font_size: layout.title?.font_size,
      variable: 'No',
    });
  }

  // Table element — from visualization or data presence
  const viz = (slide.visualization || '').toLowerCase();
  if (layout.table && (viz === 'table' || viz === 'ultra' || slide.data)) {
    const pos = toPixels(layout.table);
    // Convert row-based data to column format if needed
    const columns = slide.columns || (Array.isArray(slide.data) ? convertRowDataToColumns(slide.data) : []);
    elements.push({
      type: 'table',
      ...pos,
      data_source: slide.source || '',
      query: slide.query || '',
      query_prompt: slide.prompt || '',
      visualization: 'table',
      variable: 'No',
      columns,
    });
  }

  // Chart element
  if (layout.chart && (viz === 'chart' || viz === 'ultra' || slide.chart)) {
    const pos = toPixels(layout.chart);
    // Build chart categories/series from row data
    const chartData = Array.isArray(slide.data) ? buildChartDataFromRows(slide.data) : { categories: [], series: [] };
    elements.push({
      type: 'chart',
      ...pos,
      data_source: slide.source || '',
      query: slide.query || '',
      query_prompt: slide.prompt || '',
      visualization: slide.chart || 'bar',
      chart_type: slide.chart || 'bar',
      variable: 'No',
      categories: chartData.categories,
      series: chartData.series,
    });
  }

  // Comment / text element
  if (layout.comment && (slide.has_comment || slide.comment)) {
    const pos = toPixels(layout.comment);
    elements.push({
      type: 'text',
      ...pos,
      text: slide.comment || '',
      variable: 'No',
    });
  }

  return { elements };
}

/**
 * Get the slides array from items, regardless of format.
 */
export function getSlidesFromItems(items) {
  if (!items) return [];
  if (Array.isArray(items)) {
    // Old format: items IS the slides array
    return items;
  }
  if (typeof items === 'object' && Array.isArray(items.slides)) {
    // Wizard format: slides are nested
    return items.slides;
  }
  return [];
}

/**
 * Get the slide count from items regardless of format.
 */
export function getSlidesCount(items) {
  return getSlidesFromItems(items).length;
}

/**
 * Normalize element position: convert bbox array [x,y,w,h] to flat x/y/width/height props.
 * Passes through elements that already have flat props. Does not mutate input.
 */
function normalizeElementPosition(element) {
  if (Array.isArray(element.bbox) && element.bbox.length >= 4) {
    const { bbox, ...rest } = element;
    return {
      ...rest,
      x: Number(bbox[0]) || 0,
      y: Number(bbox[1]) || 0,
      width: Number(bbox[2]) || 0,
      height: Number(bbox[3]) || 0,
    };
  }
  return element;
}

/**
 * Normalize all elements in a wizard-format slide to use flat position props.
 */
function normalizeSlideElements(slide) {
  if (!slide || !Array.isArray(slide.elements)) return slide;
  return {
    ...slide,
    elements: slide.elements.map(normalizeElementPosition),
  };
}

/**
 * Normalize a presentation's items into consistent wizard format.
 * Returns the normalized items object (does not mutate input).
 */
export function normalizeItems(items) {
  if (!items) {
    return { description: '', report_date_context: '', slides: [] };
  }

  // Already wizard format — normalize element positions (bbox → flat props)
  if (!Array.isArray(items) && typeof items === 'object' && Array.isArray(items.slides)) {
    return {
      ...items,
      slides: items.slides.map(normalizeSlideElements),
    };
  }

  // Old format: items is an array of slide objects
  if (Array.isArray(items)) {
    return {
      description: '',
      report_date_context: '',
      slides: items.map(convertOldSlide),
    };
  }

  // String (might be JSON)
  if (typeof items === 'string') {
    try {
      return normalizeItems(JSON.parse(items));
    } catch {
      return { description: '', report_date_context: '', slides: [] };
    }
  }

  return { description: '', report_date_context: '', slides: [] };
}

/**
 * Normalize a full presentation object.
 * Adds slides_count and normalized_items fields.
 */
export function normalizePresentation(presentation) {
  if (!presentation) return presentation;

  let slides = presentation.slides;
  if (typeof slides === 'string') {
    try { slides = JSON.parse(slides); } catch { slides = null; }
  }

  const normalizedItems = normalizeItems(slides);

  return {
    ...presentation,
    slides, // keep original parsed slides
    slides_count: presentation.slides_count ?? normalizedItems.slides.length,
    normalized_items: normalizedItems,
  };
}

export default normalizePresentation;
